/* gb-hub.js (Gift Basket Delivery, 7 Oct 2026; ported from Flowers Now's fn-hub-tabs.js).
   The shopping hub on landing collection pages (sections/gb-hub.liquid).
   1. Steps: the photo tiles above a product grid act as tabs. A tile loads its fragment
      (/collections/{handle}?section_id=gb-hub-products, or this page's collection with a price filter for price tiles)
      once and swaps the grid and its buttons in place. Each tile keeps what the shopper opened while they look at the
      others. The first tile's products come with the page. CSS keeps `rows` rows of the first group on screen at each
      width; "View N more" opens the rest (loading it when the page came with the first group only) and then goes away;
      its count follows the screen width. "Shop all" stays. A tile's note shows above its products. A tile with a second
      row shows that row while it or one of its tiles is chosen. A tile row wider than the screen gets a fade and an
      arrow at its right edge until it is scrolled to the end. Without JavaScript the tiles are plain links.
   2. Shopping paths: a path scrolls to its step; once the step's tile row is in view, a thin line traces around it and
      fades, so the eye lands on the tiles to choose from. Reduced motion turns the animation off in gb-hub.css.
   3. Card messages: tap to copy to the clipboard. */
(function () {
  'use strict';
  if (window.gbHub) return;
  window.gbHub = true;
  var d = document;
  var PARTS = '[data-gbhub-grid], [data-gbhub-more]';
  var pages = {};
  var pending = {};
  var counters = [];
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function arr(list) { return Array.prototype.slice.call(list); }
  function tick() {}
  function get(url) {
    if (pages[url]) return Promise.resolve(pages[url]);
    if (pending[url]) return pending[url];
    pending[url] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error(String(r.status))); })
      .then(function (html) { pages[url] = html; delete pending[url]; return html; },
            function (err) { delete pending[url]; throw err; });
    return pending[url];
  }
  function cardUrl(card) {
    var a = card.querySelector('a[href]');
    return a ? a.getAttribute('href').split('?')[0] : '';
  }
  function cardsOf(grid) { return arr(grid.querySelectorAll(':scope > li')); }
  function isShown(card) { return !card.hidden && window.getComputedStyle(card).display !== 'none'; }
  // Scroll hint: a tile row that overflows gets a fade and an arrow at its right edge (hidden at the end). The hint is a
  // sibling of the row, positioned over the row's right edge from the .gbhub-nav wrapper (8 Oct 2026: the earlier sticky
  // list item stopped short of the clip edge on iPhones and left a strip of the next tile showing beside the fade).
  var hints = [];
  function hint(row) {
    var nav = row.parentNode;
    if (!nav || row.nextElementSibling && row.nextElementSibling.classList.contains('gbhub-nav__hint')) return;
    var box = d.createElement('div');
    box.className = 'gbhub-nav__hint';
    box.setAttribute('aria-hidden', 'true');
    var btn = d.createElement('button');
    btn.type = 'button';
    btn.className = 'gbhub-nav__arrow';
    btn.tabIndex = -1;
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M9 6l6 6-6 6"/></svg>';
    btn.addEventListener('click', function () {
      var by = Math.max(120, Math.round(row.clientWidth * 0.75));
      try { row.scrollBy({ left: by, behavior: 'smooth' }); } catch (err) { row.scrollLeft += by; }
    });
    box.appendChild(btn);
    nav.insertBefore(box, row.nextSibling);
    function update() {
      var over = !row.hidden && row.scrollWidth - row.clientWidth > 8;
      var end = row.scrollLeft + row.clientWidth >= row.scrollWidth - 8;
      row.classList.toggle('is-overflow', over);
      row.classList.toggle('is-end', end);
      if (over) {
        box.style.top = row.offsetTop + 'px';
        box.style.height = row.offsetHeight + 'px';
      }
    }
    row.addEventListener('scroll', update, { passive: true });
    hints.push(update);
    update();
  }
  function rehint() { hints.forEach(function (fn) { fn(); }); }
  window.addEventListener('resize', rehint);

  function init(root) {
    if (root.getAttribute('data-gbhub-ready')) return;
    var panel = root.querySelector('[data-gbhub-panel]');
    var tabs = arr(root.querySelectorAll('[data-gbhub-tab]'));
    if (!panel || !tabs.length) return;
    root.setAttribute('data-gbhub-ready', '1');
    var first = parseInt(root.getAttribute('data-gbhub-first'), 10) || 0;
    var rows = root.getAttribute('data-gbhub-rows') || '';
    var list = root.querySelector('[data-gbhub-tablist]');
    var loading = panel.querySelector('[data-gbhub-loading]');
    var note = panel.querySelector('[data-gbhub-notebox]');
    var saved = {};
    var want = null;
    if (list) {
      list.setAttribute('role', 'tablist');
      each(list.children, function (li) { li.setAttribute('role', 'presentation'); });
    }
    var subs = arr(root.querySelectorAll('[data-gbhub-sub]'));
    subs.forEach(function (ul) { ul.setAttribute('role', 'tablist'); each(ul.children, function (li) { li.setAttribute('role', 'presentation'); }); });
    if (list) hint(list);
    subs.forEach(hint);
    function groupOf(tab) {
      var parent = tab.getAttribute('data-gbhub-parent');
      if (parent) return parent;
      var key = tab.getAttribute('data-gbhub-tab');
      return subs.some(function (ul) { return ul.getAttribute('data-gbhub-sub') === key; }) ? key : '';
    }
    function showSub(tab) {
      var group = groupOf(tab);
      subs.forEach(function (ul) { ul.hidden = ul.getAttribute('data-gbhub-sub') !== group; });
      rehint();
      tabs.forEach(function (t) { t.classList.toggle('is-parent', !!group && !t.getAttribute('data-gbhub-parent') && t.getAttribute('data-gbhub-tab') === group && t !== tab); });
    }
    function visibleTabs() { return tabs.filter(function (t) { return t.offsetParent !== null; }); }
    panel.setAttribute('role', 'tabpanel');
    var active = tabs.filter(function (t) { return t.classList.contains('is-active'); })[0] || tabs[0];
    var shown = active.getAttribute('data-gbhub-tab');
    function grid() { return panel.querySelector('[data-gbhub-grid]'); }
    // Fragments carry every product the tile holds; this step shows `first` before View more.
    function normalize() {
      var g = grid();
      if (!g || g.classList.contains('is-open')) return;
      if (rows) g.setAttribute('data-rows', rows);
      if (!first) return;
      cardsOf(g).forEach(function (c, i) { c.hidden = i >= first; });
    }
    // View N more: what the tile holds minus what shows at this screen width.
    function count() {
      var g = grid();
      var btn = panel.querySelector('[data-gbhub-morebtn]');
      if (!g || !btn || g.classList.contains('is-open')) return;
      var total = parseInt(g.getAttribute('data-total'), 10);
      if (!(total >= 0)) return;
      var n = total - cardsOf(g).filter(isShown).length;
      btn.hidden = n <= 0;
      var tpl = btn.getAttribute('data-tpl');
      if (tpl && n > 0) btn.textContent = tpl.replace('{n}', String(n));
    }
    counters.push(count);
    function mark(tab) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('role', 'tab');
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      panel.setAttribute('aria-labelledby', tab.id);
      showSub(tab);
    }
    function setNote(tab) {
      if (!note) return;
      var text = tab.getAttribute('data-gbhub-note') || '';
      note.textContent = text;
      note.hidden = !text;
    }
    function busy(on) {
      if (loading) loading.hidden = !on;
      if (on) panel.setAttribute('aria-busy', 'true'); else panel.removeAttribute('aria-busy');
    }
    function snapshot() {
      if (!shown) return;
      each(panel.querySelectorAll('[data-gbhub-morebtn]'), function (b) { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); });
      saved[shown] = arr(panel.querySelectorAll(PARTS)).map(function (n) { return n.outerHTML; }).join('');
    }
    function render(html, key, tab) {
      each(panel.querySelectorAll(PARTS), function (n) { n.parentNode.removeChild(n); });
      var tmp = d.createElement('div');
      tmp.innerHTML = html;
      var frag = d.createDocumentFragment();
      arr(tmp.querySelectorAll(PARTS)).forEach(function (n) { frag.appendChild(n); });
      panel.insertBefore(frag, loading || null);
      shown = key;
      normalize();
      var allUrl = tab && tab.getAttribute('data-all-url');
      var all = panel.querySelector('.gbhub__all');
      if (allUrl && all) all.setAttribute('href', allUrl);
      count();
      tick();
    }
    function load(tab) {
      var key = tab.getAttribute('data-gbhub-tab');
      want = key;
      if (saved[key]) { busy(false); render(saved[key], key, tab); return; }
      var url = tab.getAttribute('data-url');
      if (!url) { busy(false); return; }
      busy(true);
      get(url).then(function (html) {
        if (want !== key) return;
        busy(false);
        render(html, key, tab);
      }).catch(function () {
        if (want !== key) return;
        busy(false);
        window.location.href = tab.href;
      });
    }
    function choose(tab, focus) {
      if (tab === active) return;
      snapshot();
      active = tab;
      mark(tab);
      setNote(tab);
      if (focus) tab.focus();
      load(tab);
    }
    mark(active);
    normalize();
    count();
    tabs.forEach(function (t) {
      t.addEventListener('click', function (e) {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        choose(t, false);
      });
      t.addEventListener('keydown', function (e) {
        var vis = visibleTabs();
        var k = vis.indexOf(t);
        var j;
        if (k < 0) return;
        if (e.key === 'ArrowRight') j = (k + 1) % vis.length;
        else if (e.key === 'ArrowLeft') j = (k - 1 + vis.length) % vis.length;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = vis.length - 1;
        else return;
        e.preventDefault();
        choose(vis[j], true);
      });
    });
    panel.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-gbhub-morebtn]') : null;
      if (!btn) return;
      e.preventDefault();
      if (panel.getAttribute('aria-busy') === 'true' || btn.classList.contains('is-loading')) return;
      var g = grid();
      if (!g) return;
      var before = cardsOf(g).filter(isShown);
      function open() {
        g.classList.add('is-open');
        cardsOf(g).forEach(function (c) { c.hidden = false; });
        if (btn.parentNode) btn.parentNode.removeChild(btn);
        tick();
        var fresh = cardsOf(g).filter(function (c) { return before.indexOf(c) < 0; })[0];
        var link = fresh ? fresh.querySelector('a[href]') : null;
        if (link) link.focus({ preventScroll: true });
      }
      var total = parseInt(g.getAttribute('data-total'), 10) || 0;
      var url = btn.getAttribute('data-url');
      if (!url || cardsOf(g).length >= total) { open(); return; }
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      get(url).then(function (html) {
        if (!g.isConnected) return;
        var have = {};
        cardsOf(g).forEach(function (c) { have[cardUrl(c)] = true; });
        var tmp = d.createElement('div');
        tmp.innerHTML = html;
        arr(tmp.querySelectorAll('[data-gbhub-grid] > li')).forEach(function (c) {
          var u = cardUrl(c);
          if (have[u]) return;
          have[u] = true;
          g.appendChild(c);
        });
        open();
      }).catch(function () {
        if (!g.isConnected) return;
        var all = panel.querySelector('.gbhub__all');
        if (all) window.location.href = all.href;
      });
    });
  }
  function recount() { counters.forEach(function (fn) { fn(); }); }
  if (window.matchMedia) {
    ['(min-width: 750px)', '(min-width: 990px)'].forEach(function (q) {
      var m = window.matchMedia(q);
      if (m.addEventListener) m.addEventListener('change', recount); else if (m.addListener) m.addListener(recount);
    });
  }

  // Shopping paths: the line that traces the tile row of the step they chose.
  function trace(section) {
    var nav = section.querySelector('.gbhub-nav');
    if (!nav) return;
    var old = nav.querySelector('.gbhub-trace');
    if (old) old.parentNode.removeChild(old);
    var svg = d.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'gbhub-trace');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var rect = d.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('pathLength', '1');
    rect.setAttribute('rx', '16'); rect.setAttribute('ry', '16');
    svg.appendChild(rect);
    nav.appendChild(svg);
    var box = nav.getBoundingClientRect();
    var tiles = nav.querySelectorAll('[data-gbhub-tablist] > li, [data-gbhub-sub]:not([hidden]) > li');
    var l = Infinity, t = Infinity, r = -Infinity, b = -Infinity;
    each(tiles, function (li) {
      var c = li.getBoundingClientRect();
      if (!c.width) return;
      l = Math.min(l, c.left); t = Math.min(t, c.top); r = Math.max(r, c.right); b = Math.max(b, c.bottom);
    });
    if (!isFinite(l)) { l = box.left; t = box.top; r = box.right; b = box.bottom; }
    var pad = 8;
    l = Math.max(box.left, l - pad); t = Math.max(box.top - 4, t - pad); r = Math.min(box.right, r + pad); b = Math.min(box.bottom + 4, b + pad);
    var w = nav.clientWidth, h = nav.clientHeight;
    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    svg.style.width = w + 'px'; svg.style.height = h + 'px';
    rect.setAttribute('x', String(l - box.left)); rect.setAttribute('y', String(t - box.top));
    rect.setAttribute('width', String(Math.max(0, r - l)));
    rect.setAttribute('height', String(Math.max(0, b - t)));
    var done = function () { if (svg.parentNode) svg.parentNode.removeChild(svg); };
    svg.addEventListener('animationend', done);
    setTimeout(done, 3200);
  }
  function traceWhenVisible(section) {
    if (!('IntersectionObserver' in window)) { trace(section); return; }
    var fired = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (fired || !e.isIntersecting || e.intersectionRatio < 0.6) return;
        fired = true; io.disconnect();
        setTimeout(function () { trace(section); }, 150);
      });
    }, { threshold: [0.6] });
    io.observe(section.querySelector('.gbhub-nav') || section);
    var tries = 0;
    (function check() {
      if (fired) return;
      var c = (section.querySelector('.gbhub-nav') || section).getBoundingClientRect();
      var vh = window.innerHeight || d.documentElement.clientHeight;
      if (c.top >= 0 && c.bottom <= vh) { fired = true; io.disconnect(); trace(section); return; }
      if (++tries < 12) setTimeout(check, 250); else { fired = true; io.disconnect(); }
    })();
  }
  function stepFor(hash) {
    if (!hash || hash.charAt(0) !== '#') return null;
    var el = d.getElementById(hash.slice(1));
    return el && el.hasAttribute('data-gbhub-step') ? el : null;
  }
  d.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('.gbhub-paths__item[href^="#"]') : null;
    if (!a) return;
    var section = stepFor(a.getAttribute('href'));
    if (!section) return;
    // The theme's sticky header would hide the top of the step: scroll so the question line sits under it.
    e.preventDefault();
    var header = d.querySelector('.header.is-sticky, .header-mobile.is-sticky, [data-gb-sticky-header]');
    var offset = header ? header.getBoundingClientRect().height + 12 : 16;
    var y = section.getBoundingClientRect().top + window.pageYOffset - offset;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' }); } catch (err) { window.scrollTo(0, y); }
    if (history.replaceState) history.replaceState(null, '', a.getAttribute('href'));
    traceWhenVisible(section);
  });
  window.addEventListener('hashchange', function () { var s = stepFor(location.hash); if (s) traceWhenVisible(s); });

  // Card messages: tap to copy.
  d.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-gbhub-copy]') : null;
    if (!btn) return;
    var text = btn.getAttribute('data-gbhub-copy') || '';
    var label = btn.querySelector('[data-gbhub-copy-label]');
    var before = label ? label.textContent : '';
    function done() {
      btn.classList.add('is-copied');
      if (label) label.textContent = btn.getAttribute('data-done') || 'Copied';
      setTimeout(function () { btn.classList.remove('is-copied'); if (label) label.textContent = before; }, 1800);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function () { fallback(); });
    } else { fallback(); }
    function fallback() {
      var ta = d.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      d.body.appendChild(ta); ta.select();
      try { d.execCommand('copy'); done(); } catch (err) { /* nothing to do */ }
      d.body.removeChild(ta);
    }
  });

  function start(scope) {
    each((scope || d).querySelectorAll('[data-gbhub-step]'), init);
    if (!scope) { var s = stepFor(location.hash); if (s) traceWhenVisible(s); }
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { start(); }, { once: true }); else start();
  d.addEventListener('shopify:section:load', function (e) { start(e.target); });
})();
